import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type AdminNotification = {
  id: string;
  type: string;
  title: string;
  body: string;
  link_path: string | null;
  read_at: string | null;
  created_at: string;
};

const KEY = ['admin-notifications'];

/**
 * Polled rather than pushed. The console has no socket, and a 60s refresh is
 * well inside the time it takes an admin to act on a KYC submission — worth
 * revisiting only if this ever needs to feel live.
 */
export function use_notifications(enabled = true) {
  return useQuery({
    queryKey: [...KEY, 'list'],
    queryFn: async () => {
      const res = await api.get<{ items: AdminNotification[] }>('/admin/notifications', {
        params: { limit: 30 },
      });
      return res.data.items;
    },
    enabled,
    refetchInterval: 60_000,
  });
}

export function use_unread_count() {
  return useQuery({
    queryKey: [...KEY, 'unread'],
    queryFn: async () => {
      const res = await api.get<{ unread: number }>('/admin/notifications/unread-count');
      return res.data.unread;
    },
    refetchInterval: 60_000,
  });
}

export function use_mark_read() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/admin/notifications/${id}/read`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function use_mark_all_read() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/admin/notifications/read-all'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}
